/**
 * Cache utility for storing and retrieving cached data with expiration
 */

const CACHE_PREFIX = 'app_cache_';
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes in milliseconds

export const cacheUtils = {
  /**
   * Set cache with timestamp
   * @param {string} key - Cache key
   * @param {any} data - Data to cache
   * @param {number} duration - Cache duration in milliseconds (default: 10 minutes)
   */
  setCache: (key, data, duration = CACHE_DURATION) => {
    try {
      const cacheData = {
        data,
        timestamp: Date.now(),
        expiry: Date.now() + duration,
      };
      localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Failed to set cache:', error);
    }
  },

  /**
   * Get cache if not expired
   * @param {string} key - Cache key
   * @returns {any} Cached data or null if expired/not found
   */
  getCache: (key) => {
    try {
      const cached = localStorage.getItem(CACHE_PREFIX + key);
      if (!cached) return null;

      const cacheData = JSON.parse(cached);
      
      // Check if cache has expired
      if (Date.now() > cacheData.expiry) {
        cacheUtils.clearCache(key);
        return null;
      }

      return cacheData.data;
    } catch (error) {
      console.warn('Failed to get cache:', error);
      return null;
    }
  },

  /**
   * Get cache timestamp
   * @param {string} key - Cache key
   * @returns {number} Timestamp when cache was created, or null
   */
  getCacheTimestamp: (key) => {
    try {
      const cached = localStorage.getItem(CACHE_PREFIX + key);
      if (!cached) return null;

      const cacheData = JSON.parse(cached);
      
      // Check if cache has expired
      if (Date.now() > cacheData.expiry) {
        return null;
      }

      return cacheData.timestamp;
    } catch (error) {
      console.warn('Failed to get cache timestamp:', error);
      return null;
    }
  },

  /**
   * Clear specific cache
   * @param {string} key - Cache key
   */
  clearCache: (key) => {
    try {
      localStorage.removeItem(CACHE_PREFIX + key);
    } catch (error) {
      console.warn('Failed to clear cache:', error);
    }
  },

  /**
   * Clear all app caches
   */
  clearAllCache: () => {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        if (key.startsWith(CACHE_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('Failed to clear all caches:', error);
    }
  },

  /**
   * Get time ago string (e.g., "5 minutes ago")
   * @param {number} timestamp - Timestamp in milliseconds
   * @param {string} locale - Locale string (e.g., 'en-IN', 'te-IN')
   * @param {Function} t - Translation function (optional)
   * @returns {string} Human-readable time string
   */
  getTimeAgo: (timestamp, locale = 'en-IN', t = null) => {
    if (!timestamp) return '';
    
    const now = Date.now();
    const diff = now - timestamp;
    
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    try {
      const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

      if (seconds < 60) return t ? t('common.justNow') : 'just now';
      if (minutes < 60) return formatter.format(-minutes, 'minute');
      if (hours < 24) return formatter.format(-hours, 'hour');
      if (days < 7) return formatter.format(-days, 'day');

      const weeks = Math.floor(days / 7);
      return formatter.format(-weeks, 'week');
    } catch (_error) {
      if (seconds < 60) return t ? t('common.justNow') : 'just now';
      if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
      if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
      if (days < 7) return `${days} day${days !== 1 ? 's' : ''} ago`;
      return 'A while ago';
    }
  },
};

export default cacheUtils;
